import { BASE_API_URL } from '../App';

export async function subscribeToPushNotifications() {
  try {
    console.log('Starting subscription process...');
    
    // Check if service workers and push messaging is supported
    if (!('serviceWorker' in navigator) || !('PushManager' in window)) {
      throw new Error('Push notifications are not supported');
    }

    // Detect platforms
    const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
    const isMacOS = /Macintosh/.test(navigator.userAgent) && !window.MSStream;
    const isStandalone = window.matchMedia('(display-mode: standalone)').matches;
    console.log('Platform detection:', { isIOS, isMacOS, isStandalone });
    
    // iOS requires the app to be added to the home screen
    if (isIOS && !isStandalone) {
      throw new Error('On iOS, you must add this app to your home screen for notifications to work');
    }
    
    // Check iOS version for web push support (needs 16.4+)
    if (isIOS) {
      const versionMatch = navigator.userAgent.match(/OS (\d+)_(\d+)_?(\d+)?/);
      if (versionMatch) {
        const major = parseInt(versionMatch[1], 10);
        const minor = parseInt(versionMatch[2], 10);
        
        if (major < 16 || (major === 16 && minor < 4)) {
          throw new Error('Push notifications require iOS 16.4 or newer');
        }
      }
    }
    
    // Check macOS Safari version for web push support (needs 16.4+)
    if (isMacOS) {
      const safariVersionMatch = navigator.userAgent.match(/Version\/(\d+)\.(\d+)(?:\.(\d+))?.*Safari/);
      if (safariVersionMatch) {
        const major = parseInt(safariVersionMatch[1], 10);
        const minor = parseInt(safariVersionMatch[2], 10);
        
        console.log(`macOS Safari version: ${major}.${minor}`);
        
        if (major < 16 || (major === 16 && minor < 4)) {
          throw new Error('Push notifications on macOS require Safari 16.4 or newer');
        }
      }
    }

    // Get the VAPID public key from the server
    console.log('Fetching VAPID public key...');
    let response;
    try {
      response = await fetch(`${BASE_API_URL}/api/vapid-public-key`, {
        credentials: 'include',  // Include cookies for authentication
      });
    } catch (err) {
      console.error('Network error fetching VAPID key:', err);
      throw new Error('Network error connecting to server. Please check your connection.');
    }
    
    if (!response.ok) {
      console.error('Failed to get VAPID public key:', response.status, response.statusText);
      throw new Error(`Failed to get VAPID public key: ${response.status} ${response.statusText}`);
    }
    
    // Handle text response for non-jsonified endpoint
    const responseText = await response.text();
    console.log('VAPID key response:', responseText.substring(0, 20) + '...');
    
    let publicKey;
    
    try {
      // Try to parse as JSON first
      const data = JSON.parse(responseText);
      publicKey = data.publicKey;
      console.log('Parsed VAPID key from JSON:', publicKey ? 'success' : 'missing');
    } catch (e) {
      // If parsing fails, use the raw text
      publicKey = responseText;
      console.log('Using raw text as VAPID key');
    }
    
    if (!publicKey) {
      throw new Error('Invalid VAPID public key received');
    }
    
    // Clean the key - remove any whitespace, quotes or newlines
    publicKey = publicKey.replace(/[\s"']/g, '');
    
    if (publicKey.length < 20) {
      console.error('VAPID key too short:', publicKey);
      throw new Error('Invalid VAPID public key: key too short');
    }
    
    console.log('Got VAPID public key:', publicKey.substring(0, 10) + '...');

    // Register service worker if not already registered
    let registration;
    try {
      console.log('Getting service worker registration...');
      // First try to get the registration for the specific scope we want
      registration = await navigator.serviceWorker.getRegistration('/');
      
      if (!registration) {
        console.log('No registration found, registering new service worker...');
        try {
          // Force update the service worker for iOS
          navigator.serviceWorker.addEventListener('controllerchange', () => {
            console.log('Service worker controller changed');
          });
          
          registration = await navigator.serviceWorker.register('/service-worker.js', {
            scope: '/',
            updateViaCache: 'none' // Prevent caching issues especially on iOS
          });
          console.log('Service worker registered, waiting for it to be ready...');
          await navigator.serviceWorker.ready;
        } catch (regErr) {
          console.error('Service worker registration failed:', regErr);
          throw new Error(`Service worker registration failed: ${regErr.message}`);
        }
      } else {
        console.log('Found existing service worker registration:', registration.scope);
      }
    } catch (err) {
      console.error('Error with service worker:', err);
      throw new Error('Failed to set up service worker: ' + err.message);
    }

    // Get the push subscription
    console.log('Checking for existing push subscription...');
    let subscription;
    try {
      subscription = await registration.pushManager.getSubscription();
      console.log('Existing subscription:', subscription ? 'found' : 'none');
    } catch (subErr) {
      console.error('Error getting existing subscription:', subErr);
      throw new Error('Failed to check existing subscription: ' + subErr.message);
    }

    // If no subscription exists, create one
    if (!subscription) {
      console.log('Creating new push subscription...');
      try {
        // Convert the application server key using our conversion function
        const applicationServerKey = urlBase64ToUint8Array(publicKey);
        console.log('Application server key prepared:', applicationServerKey.length, 'bytes');
        
        // Create a subscription with the push manager
        subscription = await registration.pushManager.subscribe({
          userVisibleOnly: true,
          applicationServerKey: applicationServerKey
        });
        console.log('Successfully created push subscription');
      } catch (err) {
        console.error('Failed to create push subscription:', err);
        
        // Check if this is a permission error
        if (err.name === 'NotAllowedError') {
          throw new Error('Push notification permission denied by browser. Please check your browser settings.');
        }
        
        throw new Error('Failed to create push subscription: ' + err.message);
      }
    }

    // Get user ID from localStorage
    const userId = localStorage.getItem('userId');
    if (!userId) {
      throw new Error('User ID not found. Please try logging out and back in.');
    }

    // Convert the keys to base64 properly
    const p256dhKey = subscription.getKey('p256dh');
    const authKey = subscription.getKey('auth');
    
    if (!p256dhKey || !authKey) {
      console.error('Missing subscription keys:', { p256dhKey, authKey });
      throw new Error('Invalid push subscription: missing required keys');
    }
    
    // Correctly convert ArrayBuffer to base64 string
    const p256dhBase64 = arrayBufferToBase64(p256dhKey);
    const authBase64 = arrayBufferToBase64(authKey);
    
    console.log('Keys converted to base64 format:', {
      p256dhLength: p256dhBase64.length,
      authLength: authBase64.length
    });

    // Send the subscription to the server
    console.log('Sending subscription to server...');
    let subscribeResponse;
    try {
      subscribeResponse = await fetch(`${BASE_API_URL}/api/push-subscription`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          subscription: {
            endpoint: subscription.endpoint,
            keys: {
              p256dh: p256dhBase64,
              auth: authBase64
            }
          },
          user_id: userId,
          // Add email if available for better user creation
          email: localStorage.getItem('userEmail')
        }),
        credentials: 'include'
      });
    } catch (fetchErr) {
      console.error('Network error storing subscription:', fetchErr);
      throw new Error('Network error saving subscription. Please check your connection.');
    }

    if (!subscribeResponse.ok) {
      let errorText;
      try {
        const errorData = await subscribeResponse.json();
        errorText = errorData.error || `HTTP error ${subscribeResponse.status}`;
      } catch (e) {
        errorText = await subscribeResponse.text();
      }
      console.error('Server response error:', errorText);
      throw new Error(`Failed to store subscription: ${errorText}`);
    }
    
    const responseData = await subscribeResponse.json();
    console.log('Successfully stored subscription on server:', responseData);

    return true;
  } catch (err) {
    console.error('Error in subscribeToPushNotifications:', err);
    throw err;
  }
}

export async function unsubscribeFromPushNotifications() {
  try {
    const registration = await navigator.serviceWorker.getRegistration('/');
    if (!registration) {
      console.log('No service worker registration found');
      return false;
    }

    const subscription = await registration.pushManager.getSubscription();
    if (!subscription) {
      console.log('No subscription found');
      return false;
    }

    // Get user ID from localStorage
    const userId = localStorage.getItem('userId');
    if (!userId) {
      throw new Error('User ID not found. Please try logging out and back in.');
    }

    // Remove subscription from server
    console.log('Removing subscription from server...');
    const response = await fetch(`${BASE_API_URL}/api/push-subscription`, {
      method: 'DELETE',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        endpoint: subscription.endpoint,
        user_id: userId
      }),
      credentials: 'include'
    });

    if (!response.ok) {
      const errorText = await response.text();
      console.error('Server error removing subscription:', errorText);
    } else {
      console.log('Successfully removed subscription from server');
    }

    // Unsubscribe from push manager
    const result = await subscription.unsubscribe();
    console.log('Unsubscribed from push manager:', result);
    return result;
  } catch (err) {
    console.error('Error unsubscribing from push notifications:', err);
    throw err;
  }
}

export async function isPushNotificationEnabled() {
  try {
    console.log('Checking if push notifications are supported...');
    
    // IMPORTANT: Check if we already have a cached result - ONE CHECK PER SESSION
    if (typeof window._pushNotificationStatusCache !== 'undefined') {
      console.log('Using cached push notification status:', window._pushNotificationStatusCache);
      return window._pushNotificationStatusCache;
    }
    
    // Set flag to indicate check is in progress
    window._subscriptionCheckInProgress = true;
    
    // Check if service workers and push are supported
    if (!('serviceWorker' in navigator) || !('PushManager' in window)) {
      console.log('Push notifications not supported');
      window._pushNotificationStatusCache = false;
      window._subscriptionCheckInProgress = false;
      return false;
    }

    // Check if notifications are denied
    console.log('Checking notification permission:', Notification.permission);
    if (Notification.permission === 'denied') {
      console.log('Notification permission denied');
      window._pushNotificationStatusCache = false;
      return false;
    }

    // Get user ID from localStorage
    const userId = localStorage.getItem('userId');
    if (!userId) {
      console.log('No user ID found');
      window._pushNotificationStatusCache = false;
      return false;
    }
    
    // Set a timestamp to track when we did the server check
    window._lastServerSubscriptionCheck = Date.now();

    // First check with the server if the user has any active subscriptions
    try {
      console.log('Checking active subscriptions on server first...');
      const serverResponse = await fetch(`${BASE_API_URL}/api/push-subscription/verify`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          user_id: userId
        }),
        credentials: 'include'
      });

      if (serverResponse.ok) {
        const serverData = await serverResponse.json();
        console.log('Server subscription check response:', serverData);
        
        if (serverData.hasSubscription) {
          console.log('Server indicates user has an active subscription');
          
          // Cache the result
          window._pushNotificationStatusCache = true;
          
          // Get service worker registration to check browser state
          let registration;
          try {
            registration = await navigator.serviceWorker.getRegistration('/');
          } catch (err) {
            console.error('Error getting service worker registration:', err);
            // Don't return yet, we'll try to reconnect the subscription
          }
          
          if (registration) {
            // Check if browser has a subscription
            let browserSubscription;
            try {
              browserSubscription = await registration.pushManager.getSubscription();
            } catch (err) {
              console.error('Error getting browser subscription:', err);
            }
            
            if (!browserSubscription) {
              // User has active server subscription but no browser subscription
              // We'll try to reconnect during the next reconnectPushSubscription call
              console.log('Server has active subscription but browser does not - needs reconnection');
            }
          }
          
          // Return true regardless since server has active subscription
          window._subscriptionCheckInProgress = false;
          return true;
        }
      }
    } catch (err) {
      console.error('Error checking server subscription status:', err);
      // Continue to check browser subscription
    }

    // Get service worker registration
    console.log('Getting service worker registration...');
    let registration;
    try {
      registration = await navigator.serviceWorker.getRegistration('/');
    } catch (err) {
      console.error('Error getting service worker registration:', err);
      return false;
    }
    
    if (!registration) {
      console.log('No service worker registration found');
      return false;
    }

    // Check if there's an active subscription in the browser
    console.log('Checking for active push subscription in browser...');
    let subscription;
    try {
      subscription = await registration.pushManager.getSubscription();
    } catch (err) {
      console.error('Error getting push subscription:', err);
      return false;
    }
    
    const isEnabled = !!subscription;
    console.log('Browser push subscription status:', isEnabled);

    // If we have a browser subscription, verify it's still valid on the server
    if (isEnabled) {
      try {
        console.log('Verifying browser subscription on server...');
        const response = await fetch(`${BASE_API_URL}/api/push-subscription/verify`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            endpoint: subscription.endpoint,
            user_id: userId
          }),
          credentials: 'include'
        });

        if (!response.ok) {
          console.log('Server indicates subscription is invalid');
          return false;
        }
        
        try {
          const data = await response.json();
          console.log('Server verification response:', data);
          
          // Check if the user has any active push subscriptions
          if (data.valid && data.hasSubscription) {
            return true;
          }
          
          // If no active subscriptions, unsubscribe from browser
          if (subscription) {
            await subscription.unsubscribe();
          }
          return false;
        } catch (parseErr) {
          console.error('Error parsing server response:', parseErr);
          return false;
        }
      } catch (err) {
        console.error('Error verifying subscription:', err);
        return false;
      }
    }

    return false;
  } catch (err) {
    console.error('Error checking push notification status:', err);
    return false;
  }
}

// Helper function to convert VAPID key
function urlBase64ToUint8Array(base64String) {
  // The VAPID key uses URL-safe base64 encoding
  // Replace - with + and _ with / to convert to standard base64
  const padding = '='.repeat((4 - base64String.length % 4) % 4);
  const base64 = (base64String + padding)
    .replace(/-/g, '+')
    .replace(/_/g, '/');
  
  // Convert base64 to raw binary data
  const rawData = window.atob(base64);
  
  // Convert to Uint8Array
  const outputArray = new Uint8Array(rawData.length);
  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  
  return outputArray;
}

// Helper function to convert ArrayBuffer to base64 string
function arrayBufferToBase64(buffer) {
  if (!buffer) return '';
  
  // Create a Uint8Array from the buffer
  const bytes = new Uint8Array(buffer);
  
  // Convert to a binary string
  let binary = '';
  for (let i = 0; i < bytes.byteLength; i++) {
    binary += String.fromCharCode(bytes[i]);
  }
  
  // Convert to base64
  return window.btoa(binary);
}

// This function will reconnect an existing server-side subscription with the browser
// It should be called after successful authentication
export async function reconnectPushSubscription() {
  try {
    console.log('Starting push subscription reconnection...');
    
    // Check if reconnection is already in progress to prevent loops
    if (window._reconnectionInProgress) {
      console.log('Reconnection already in progress, skipping');
      return false;
    }
    
    window._reconnectionInProgress = true;
    
    // Add a timeout to reset the flag even if something fails
    const reconnectionTimeout = setTimeout(() => {
      window._reconnectionInProgress = false;
    }, 30000); // 30 second timeout
    
    // Check if notifications are supported and permission is granted
    if (!('serviceWorker' in navigator) || !('PushManager' in window)) {
      console.log('Push notifications not supported');
      window._reconnectionInProgress = false;
      clearTimeout(reconnectionTimeout);
      return false;
    }
    
    if (Notification.permission !== 'granted') {
      console.log('Notification permission not granted');
      window._reconnectionInProgress = false;
      clearTimeout(reconnectionTimeout);
      return false;
    }
    
    // Get user ID from localStorage
    const userId = localStorage.getItem('userId');
    if (!userId) {
      console.log('No user ID found for reconnection');
      window._reconnectionInProgress = false;
      clearTimeout(reconnectionTimeout);
      return false;
    }
    
    // First check if the user has an active subscription on the server
    try {
      console.log('Checking if user has active server subscription...');
      const serverCheckResponse = await fetch(`${BASE_API_URL}/api/push-subscription/verify`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          user_id: userId
        }),
        credentials: 'include'
      });
      
      if (!serverCheckResponse.ok) {
        console.log('Server check failed, aborting reconnection');
        return false;
      }
      
      const serverData = await serverCheckResponse.json();
      console.log('Server subscription check:', serverData);
      
      if (!serverData.hasSubscription) {
        console.log('No active server subscription found, skipping reconnection');
        return false;
      }
      
      console.log('Server has active subscription, continuing reconnection...');
    } catch (err) {
      console.error('Error checking server subscription status:', err);
      return false;
    }
    
    // Get service worker registration
    let registration;
    try {
      console.log('Getting service worker registration...');
      registration = await navigator.serviceWorker.getRegistration('/');
      
      if (!registration) {
        console.log('No service worker registration, waiting for it to register through normal process...');
        // Wait a bit to see if the service worker is currently registering
        await new Promise(resolve => setTimeout(resolve, 1000));
        
        // Check again after waiting
        registration = await navigator.serviceWorker.getRegistration('/');
        
        if (!registration) {
          console.log('Still no registration found after waiting, skipping reconnection');
          return false;
        }
      }
      
      // Make sure the service worker is ready
      if (registration.installing || registration.waiting) {
        console.log('Service worker not yet active, waiting for it...');
        await navigator.serviceWorker.ready;
      }
    } catch (err) {
      console.error('Error with service worker during reconnection:', err);
      return false;
    }
    
    // Check if there's already a subscription in the browser
    let subscription;
    try {
      console.log('Checking for existing browser subscription...');
      subscription = await registration.pushManager.getSubscription();
      
      if (subscription) {
        console.log('Browser already has a subscription, verifying with server...');
        
        // Verify the subscription against the server
        const verifyResponse = await fetch(`${BASE_API_URL}/api/push-subscription/verify`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            endpoint: subscription.endpoint,
            user_id: userId
          }),
          credentials: 'include'
        });
        
        if (verifyResponse.ok) {
          const verifyData = await verifyResponse.json();
          if (verifyData.valid) {
            console.log('Browser subscription is valid, no reconnection needed');
            return true;
          }
          
          console.log('Browser subscription is not valid on server, will create new one');
          // Unsubscribe from the invalid subscription
          await subscription.unsubscribe();
          subscription = null;
        }
      }
    } catch (err) {
      console.error('Error checking existing subscription:', err);
      // Continue to try creating a new subscription
    }
    
    // If no valid subscription, create a new one
    if (!subscription) {
      console.log('Creating new subscription...');
      
      // Get the VAPID public key from the server
      let vapidResponse;
      try {
        vapidResponse = await fetch(`${BASE_API_URL}/api/vapid-public-key`, {
          credentials: 'include',
        });
      } catch (err) {
        console.error('Network error fetching VAPID key:', err);
        window._reconnectionInProgress = false;
        clearTimeout(reconnectionTimeout);
        return false;
      }
      
      if (!vapidResponse.ok) {
        console.error('Failed to get VAPID key:', vapidResponse.status);
        return false;
      }
      
      const responseText = await vapidResponse.text();
      
      let publicKey;
      try {
        // Try to parse as JSON first
        const data = JSON.parse(responseText);
        publicKey = data.publicKey;
      } catch (e) {
        // If parsing fails, use the raw text
        publicKey = responseText;
      }
      
      if (!publicKey) {
        console.error('Invalid VAPID key received');
        return false;
      }
      
      // Clean the key
      publicKey = publicKey.replace(/[\s"']/g, '');
      
      try {
        // Convert the application server key
        const applicationServerKey = urlBase64ToUint8Array(publicKey);
        
        // Create a new subscription
        subscription = await registration.pushManager.subscribe({
          userVisibleOnly: true,
          applicationServerKey: applicationServerKey
        });
        
        console.log('Successfully created new push subscription for reconnection');
      } catch (err) {
        console.error('Failed to create push subscription during reconnection:', err);
        return false;
      }
    }
    
    // Send the subscription to the server
    console.log('Sending reconnected subscription to server...');
    
    // Convert the keys to base64
    const p256dhKey = subscription.getKey('p256dh');
    const authKey = subscription.getKey('auth');
    
    if (!p256dhKey || !authKey) {
      console.error('Missing subscription keys');
      return false;
    }
    
    const p256dhBase64 = arrayBufferToBase64(p256dhKey);
    const authBase64 = arrayBufferToBase64(authKey);
    
    try {
      const subscribeResponse = await fetch(`${BASE_API_URL}/api/push-subscription`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          subscription: {
            endpoint: subscription.endpoint,
            keys: {
              p256dh: p256dhBase64,
              auth: authBase64
            }
          },
          user_id: userId,
          email: localStorage.getItem('userEmail')
        }),
        credentials: 'include'
      });
      
      if (!subscribeResponse.ok) {
        console.error('Server error storing reconnected subscription:', subscribeResponse.status);
        return false;
      }
      
      const responseData = await subscribeResponse.json();
      console.log('Successfully stored reconnected subscription on server:', responseData);
      window._reconnectionInProgress = false;
      clearTimeout(reconnectionTimeout);
      // Update cache after successful reconnection
      window._pushNotificationStatusCache = true;
      return true;
    } catch (err) {
      console.error('Error storing reconnected subscription:', err);
      window._reconnectionInProgress = false;
      clearTimeout(reconnectionTimeout);
      return false;
    }
  } catch (err) {
    console.error('Error in reconnectPushSubscription:', err);
    window._reconnectionInProgress = false;
    return false;
  }
}